import { type FC, useCallback, useEffect, useState } from 'react'

import { Trigger as PrimitiveTrigger } from '@radix-ui/react-select'
import { useUnit } from 'effector-react'

import { SearchInput } from '@/components/ui/search-input.tsx'
import { Select, SelectContent, SelectGroup } from '@/components/ui/select.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { $fanCountryCode } from '@/models/chattingUI/chats/chats.ts'
import {
  $fanCountryName,
  $otherCountries,
  $search,
  $topPriorityCountries,
  getCountriesList,
  getCountriesListFx,
  setSearch,
  updateCountryFx,
} from '@/models/chattingUI/fanInfo/countries.ts'
import { CountrySelectItem } from '@/modules/chatting/ui/chat-window/fan-info/actions/EditCountry/CountrySelectItem.tsx'
import { classNames } from '@/utils/helpers.ts'

import ChevronDownIcon from '../../../../../icons/chevron-down.svg'
import { IconButton } from '../../../../components/icon-button.tsx'

export interface Props {}

export const EditCountry: FC<Props> = () => {
  const countryCode = useUnit($fanCountryCode)
  const countryName = useUnit($fanCountryName)
  const isCountriesLoading = useUnit(getCountriesListFx.pending)
  const isUpdateInProgress = useUnit(updateCountryFx.pending)
  const topPriorityCountries = useUnit($topPriorityCountries)
  const otherCountries = useUnit($otherCountries)
  const search = useUnit($search)
  const disabled = isUpdateInProgress || isCountriesLoading
  const [isOpen, setIsOpen] = useState(false)
  const handleCountrySelect = useCallback(async (country: string | null) => {
    setIsOpen(false)
    await updateCountryFx(country)
  }, [])

  useEffect(() => {
    getCountriesList()
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
    }
  }, [isOpen])

  return (
    <Select
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
    >
      <PrimitiveTrigger
        className="flex cursor-pointer items-center gap-2 whitespace-nowrap outline-none"
        disabled={disabled}
      >
        {countryName}
        <IconButton
          icon={ChevronDownIcon}
          iconClassName={classNames(
            'transition-transform',
            isOpen && '-rotate-180 text-primary-500',
          )}
          className="text-primary-500 h-5 w-5"
          tooltip={countryCode ? null : 'Select a Country'}
        />
      </PrimitiveTrigger>
      <SelectContent className="overflow-hidden">
        <SearchInput
          placeholder="Search"
          className="border-none"
          value={search}
          onInputChange={setSearch}
        />
        <Separator className="mb-2" />
        <SelectGroup className="visible-scrollbar-track max-h-[200px] overflow-auto">
          {!!topPriorityCountries.length &&
            topPriorityCountries.map(({ name, code, states }) => {
              return (
                <CountrySelectItem
                  key={name}
                  selected={countryCode === code}
                  openByDefault={
                    !!search || states?.some(({ code }) => code === countryCode)
                  }
                  value={code}
                  label={name}
                  disabled={disabled}
                  onClick={handleCountrySelect}
                >
                  {states?.map(({ name, code }) => (
                    <CountrySelectItem
                      className="pl-8"
                      key={name}
                      selected={countryCode === code}
                      value={code}
                      label={name}
                      disabled={disabled}
                      onClick={handleCountrySelect}
                    />
                  ))}
                </CountrySelectItem>
              )
            })}
          {!!topPriorityCountries.length && !!otherCountries.length && (
            <Separator className="my-2" />
          )}
          {!!otherCountries.length &&
            otherCountries.map(({ name, code, states }) => {
              return (
                <CountrySelectItem
                  key={name}
                  selected={countryCode === code}
                  openByDefault={
                    !!search || states?.some(({ code }) => code === countryCode)
                  }
                  value={code}
                  label={name}
                  disabled={disabled}
                  onClick={handleCountrySelect}
                >
                  {states?.map(({ name, code }) => (
                    <CountrySelectItem
                      className="pl-8"
                      key={name}
                      selected={countryCode === code}
                      value={code}
                      label={name}
                      disabled={disabled}
                      onClick={handleCountrySelect}
                    />
                  ))}
                </CountrySelectItem>
              )
            })}
          {!topPriorityCountries.length && !otherCountries.length && (
            <div className="text-title pointer-events-none py-2 text-center text-sm font-medium leading-[22px]">
              No results.
            </div>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

EditCountry.displayName = 'EditCountry'
